// Landing Page - Premium UI with Dark/Light Mode Support
// Full-bleed hero image with glassmorphism cards and scrolling footer
// V0.9.2: Centralized copy, refined social proof

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Home, ArrowRight, Shield, TrendingUp, Database, Zap, BarChart3 } from "lucide-react";
import { ThemeToggle, useTheme } from "@/components/ThemeProvider";
import { feedback } from "@/utils/feedback";

// Hero images - user's owned property photos
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/x6hz075y_edensdad._Refined_cross-sectional_architectural_photo_showing_i_34466e4d-a639-432a-823e-dfadbf330e4c.png",
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/bjy1lw5k_edensdad._Refined_cross-sectional_architectural_photo_showing_i_cf1302c0-53c3-4bc7-9b79-82907914deed.png",
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/jiw8ixlc_edensdad._cinematic_close-up_cross-view_of_luxury_British_home__aa4984b8-4df0-4c41-b9bc-f7261784d14d.png"
];

// Intelligence badges for scrolling ticker
const INTELLIGENCE_BADGES = [
  { icon: Database, text: "Land Registry Data" },
  { icon: BarChart3, text: "Market Analytics" },
  { icon: Zap, text: "Scoring Engine" },
  { icon: TrendingUp, text: "Price Trends" },
  { icon: Shield, text: "EPC Insights" },
  { icon: Home, text: "Space Potential" },
  { icon: Shield, text: "Data Protected" },
  { icon: TrendingUp, text: "Market Insights" },
];

// Floating toast notification — anonymised social proof
const TOAST_MESSAGES = [
  { area: "SW11", time: "3 mins ago" },
  { area: "SE1", time: "5 mins ago" },
  { area: "N1", time: "8 mins ago" },
  { area: "W4", time: "12 mins ago" },
  { area: "BS1", time: "15 mins ago" },
  { area: "M1", time: "18 mins ago" },
  { area: "LS1", time: "22 mins ago" },
];

function FloatingToast() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Pick a deterministic message based on current minute
    const idx = new Date().getMinutes() % TOAST_MESSAGES.length;
    setMessage(TOAST_MESSAGES[idx]);

    // Show after 3 seconds
    const showTimer = setTimeout(() => setVisible(true), 3000);
    // Hide after 8 seconds
    const hideTimer = setTimeout(() => setVisible(false), 8000);

    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);

  if (!message) return null;

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="social-proof-toast"
    >
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-lg">
        <div className="w-2 h-2 rounded-full bg-cipher-green animate-pulse" />
        <p className="text-sm text-white/80">
          A homeowner in <span className="text-white font-medium">{message.area}</span> explored their property <span className="text-white/60">{message.time}</span>
        </p>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [postcode, setPostcode] = useState("");
  const [error, setError] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleSubmit = () => {
    feedback.tap();
    const cleanPostcode = postcode.trim().toUpperCase();
    
    if (!cleanPostcode || cleanPostcode.length < 5) {
      setError("Please enter a valid UK postcode");
      feedback.error();
      return;
    }
    
    setError("");
    feedback.success();
    navigate('/start/new', { state: { postcode: cleanPostcode } });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col">
      {/* Floating social proof toast */}
      <FloatingToast />
      
      {/* Full-bleed Hero Image Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGES[0]}
          alt="Luxury British home"
          className={`w-full h-full object-cover transition-opacity duration-1000 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        {/* Gradient Overlays - stronger for dark mode */}
        <div className={`absolute inset-0 transition-colors duration-500 ${
          isDark 
            ? 'bg-gradient-to-b from-black/70 via-black/50 to-black/90' 
            : 'bg-gradient-to-b from-black/40 via-black/20 to-black/70'
        }`} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Static brand bar — cipher-gold */}
        <div className="bg-[#1a1a1a] border-b border-cipher-gold/20 py-2 px-4 flex-shrink-0">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className="flex items-center gap-2.5">
              <img src="https://customer-assets.emergentagent.com/job_4f723f94-5de1-433e-a1fc-17c2a953d5fd/artifacts/zc0jutd7_Nehemiah%20Profile%203.png" alt="StudioNPG" className="w-7 h-7 rounded" />
              <span className="text-sm font-semibold text-white/90">HiPPO</span>
              <span className="text-[10px] text-cipher-gold/60 hidden sm:inline">by StudioNPG</span>
            </div>
            <span className="text-[10px] text-white/40 hidden sm:inline">Footprint-First Property Intelligence</span>
          </div>
        </div>

        {/* Main Content - Centralized */}
        <main className="flex-1 flex flex-col justify-center cipher-container py-12">
          {/* Hero Text - Centered */}
          <div className="mb-8 sm:mb-10 animate-fade-up text-center max-w-2xl mx-auto px-2">
            <h1 className="font-poppins font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-5xl text-white leading-tight mb-3 sm:mb-4">
              Is your home working as hard as you are?
            </h1>
            <p className="font-opensans text-base sm:text-lg md:text-xl text-white/70">
              Discover your home&apos;s <span className="text-cipher-gold">hidden potential</span> — in under 2 minutes
            </p>
          </div>

          {/* Glassmorphism Input Card - Centered */}
          <div 
            className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl animate-fade-up max-w-md mx-auto w-full"
            style={{ animationDelay: '200ms' }}
          >
            <label className="block text-sm font-medium text-white/80 mb-2 sm:mb-3 text-center">
              Enter your postcode to begin
            </label>
            
            <div className="flex gap-2 sm:gap-3">
              <Input
                type="text"
                placeholder="e.g. SW1A 1AA"
                value={postcode}
                onChange={(e) => {
                  setPostcode(e.target.value.toUpperCase());
                  setError("");
                }}
                onKeyPress={handleKeyPress}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 text-lg py-6 rounded-xl focus:border-cipher-gold focus:ring-cipher-gold/30"
                data-testid="postcode-input"
              />
              <Button
                onClick={handleSubmit}
                className="bg-cipher-green hover:bg-cipher-green-light text-white px-6 py-6 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                data-testid="get-started-btn"
              >
                <span className="hidden sm:inline mr-2">Get Started</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            
            {error && (
              <p className="text-red-400 text-sm mt-3 animate-fade-up text-center">{error}</p>
            )}
          </div>

          {/* Trust line + free score indicator */}
          <div className="mt-5 animate-fade-up space-y-1.5" style={{ animationDelay: '300ms' }}>
            <p className="text-sm text-white/50 text-center">
              Built on the UK's most trusted property data
            </p>
            <p className="text-xs text-cipher-green/60 text-center font-medium" data-testid="free-score-line">
              Free instant HiPPO score — no sign-up required
            </p>
          </div>
        </main>

        {/* Footer — clean, minimal */}
        <footer className="cipher-container py-4 border-t border-white/5">
          <p className="text-center text-xs text-white/40">
            HiPPO by StudioNPG — Observational signals, not financial advice
          </p>
          <p className="text-center mt-2 space-x-3">
            {process.env.REACT_APP_PARTNER_LENS_ENABLED === 'true' && (
              <button 
                onClick={() => navigate('/partner')}
                className="text-[10px] text-white/20 hover:text-white/40 transition-colors"
                data-testid="partner-footer-link"
              >
                Professional access
              </button>
            )}
            {process.env.REACT_APP_INVESTOR_LENS_ENABLED === 'true' && (
              <>
                {process.env.REACT_APP_PARTNER_LENS_ENABLED === 'true' && (
                  <span className="text-white/10">·</span>
                )}
                <button 
                  onClick={() => navigate('/investor')}
                  className="text-[10px] text-white/20 hover:text-white/40 transition-colors"
                  data-testid="investor-footer-link"
                >
                  Investor portal
                </button>
              </>
            )}
          </p>
        </footer>
      </div>
    </div>
  );
}
